import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, User, Tenant, setCurrentTenant, getCurrentTenantId } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  tenants: Tenant[];
  currentTenant: Tenant | null;
  currentTenantSlug: string | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  hasTenant: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; status?: string }>;
  register: (email: string, password: string, name?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  switchTenant: (tenantId: number) => Promise<{ success: boolean; error?: string }>;
  switchTenantBySlug: (slug: string) => Promise<{ success: boolean; error?: string; tenant?: Tenant }>;
  getTenantBySlug: (slug: string) => Tenant | undefined;
  refreshTenants: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'shopify_accounting_token';
const TENANT_KEY = 'shopify_accounting_tenant_id';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenantState] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshTenants = useCallback(async () => {
    try {
      const response = await authAPI.getTenants();
      const fetchedTenants = response.data.tenants || [];
      setTenants(fetchedTenants);
      
      // Check if current tenant is still valid
      const currentId = getCurrentTenantId();
      if (currentId) {
        const validTenant = fetchedTenants.find((t: Tenant) => t.id === currentId);
        if (validTenant) {
          setCurrentTenantState(validTenant);
        } else if (fetchedTenants.length > 0) {
          // Current tenant no longer valid, switch to first available
          setCurrentTenant(fetchedTenants[0].id);
          setCurrentTenantState(fetchedTenants[0]);
        } else {
          // No tenants available
          setCurrentTenant(null);
          setCurrentTenantState(null);
        }
      } else if (fetchedTenants.length > 0) {
        // No current tenant set, use first available
        setCurrentTenant(fetchedTenants[0].id);
        setCurrentTenantState(fetchedTenants[0]);
      }
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      setTenants([]);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) {
      setIsLoading(false);
      return;
    }

    try {
      setToken(storedToken);
      const response = await authAPI.me();
      setUser(response.data.user);
      
      // Set tenants from me response
      const fetchedTenants = response.data.tenants || [];
      setTenants(fetchedTenants);
      
      // Restore current tenant from localStorage or set default
      const storedTenantId = getCurrentTenantId();
      if (storedTenantId) {
        const validTenant = fetchedTenants.find((t: Tenant) => t.id === storedTenantId);
        if (validTenant) {
          setCurrentTenantState(validTenant);
        } else if (fetchedTenants.length > 0) {
          // Stored tenant no longer valid, switch to first
          setCurrentTenant(fetchedTenants[0].id);
          setCurrentTenantState(fetchedTenants[0]);
        }
      } else if (fetchedTenants.length > 0) {
        // No stored tenant, use first available
        setCurrentTenant(fetchedTenants[0].id);
        setCurrentTenantState(fetchedTenants[0]);
      }
    } catch (error) {
      // Token is invalid or expired
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TENANT_KEY);
      setToken(null);
      setUser(null);
      setTenants([]);
      setCurrentTenantState(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string; status?: string }> => {
    try {
      const response = await authAPI.login(email, password);
      const { token: newToken, user: newUser, tenants: newTenants } = response.data;
      
      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
      setUser(newUser);
      
      // Set tenants
      const fetchedTenants = newTenants || [];
      setTenants(fetchedTenants);
      
      // Auto-select first tenant if available
      if (fetchedTenants.length > 0) {
        setCurrentTenant(fetchedTenants[0].id);
        setCurrentTenantState(fetchedTenants[0]);
      }
      
      return { success: true };
    } catch (error: any) {
      const errorData = error.response?.data;
      return {
        success: false,
        error: errorData?.error || 'Login failed',
        status: errorData?.status,
      };
    }
  };

  const register = async (email: string, password: string, name?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await authAPI.register(email, password, name);
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TENANT_KEY);
    setToken(null);
    setUser(null);
    setTenants([]);
    setCurrentTenantState(null);
  };

  const switchTenant = async (tenantId: number): Promise<{ success: boolean; error?: string }> => {
    try {
      // Validate the switch with the server
      const response = await authAPI.switchTenant(tenantId);
      const tenant = response.data.tenant;
      
      // Update local state
      setCurrentTenant(tenantId);
      setCurrentTenantState(tenant);
      
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to switch tenant',
      };
    }
  };

  /**
   * Get a tenant by its shop name (slug)
   */
  const getTenantBySlug = useCallback((slug: string): Tenant | undefined => {
    return tenants.find(t => t.shopify_shop_name === slug);
  }, [tenants]);

  /**
   * Switch tenant by slug (shop name) - doesn't reload the page
   */
  const switchTenantBySlug = useCallback(async (slug: string): Promise<{ success: boolean; error?: string; tenant?: Tenant }> => {
    const tenant = getTenantBySlug(slug);
    
    if (!tenant) {
      return {
        success: false,
        error: `Tenant with slug "${slug}" not found`,
      };
    }

    // If already on this tenant, just return success
    if (currentTenant?.id === tenant.id) {
      return { success: true, tenant };
    }

    try {
      // Validate the switch with the server
      const response = await authAPI.switchTenant(tenant.id);
      const validatedTenant = response.data.tenant;
      
      // Update local state
      setCurrentTenant(tenant.id);
      setCurrentTenantState(validatedTenant);
      
      return { success: true, tenant: validatedTenant };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to switch tenant',
      };
    }
  }, [getTenantBySlug, currentTenant]);

  const value: AuthContextType = {
    user,
    token,
    tenants,
    currentTenant,
    currentTenantSlug: currentTenant?.shopify_shop_name,
    isLoading,
    isAuthenticated: !!user && !!token,
    isAdmin: user?.role === 'admin',
    hasTenant: !!currentTenant,
    login,
    register,
    logout,
    refreshUser,
    switchTenant,
    switchTenantBySlug,
    getTenantBySlug,
    refreshTenants,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
